import { BadgeCheck, CreditCard, Landmark, ShieldCheck } from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { useSeo } from '@/lib/useSeo';
import { useCmsFields, CmsSchema } from '@/cms/content';
import { PLATNOSCI_FIELDS } from '@/data/platnosciContent';

const PAYMENT_METHODS = [
  {
    id: 'card',
    icon: CreditCard,
    title: 'Karta płatnicza',
    description: 'Zapłać online kartą Visa lub Mastercard podczas składania zamówienia.',
  },
  {
    id: 'blik',
    icon: BadgeCheck,
    title: 'BLIK',
    description: 'Wybierz BLIK w bezpiecznej bramce płatniczej i potwierdź transakcję w aplikacji banku.',
  },
  {
    id: 'transfer',
    icon: Landmark,
    title: 'Przelewy24',
    description: 'Skorzystaj z szybkiego przelewu online obsługiwanego przez Przelewy24.',
  },
] as const;

export default function PlatnosciPage() {
  const cms = useCmsFields(PLATNOSCI_FIELDS);
  useSeo({
    title: 'Płatności — bezpieczne płatności online',
    description: 'Sprawdź dostępne metody płatności w Bukietowa.pl: Visa, Mastercard, BLIK i Przelewy24.',
    robots: 'index, follow',
  });
  usePrerenderReady();

  return (
    <main style={{ background: '#fff', color: '#2a1f14' }}>
      <CmsSchema fields={cms.fields} />
      <section style={{ padding: 'clamp(48px, 9vw, 112px) 0 clamp(56px, 9vw, 104px)', background: '#fff' }}>
        <div className="page-container">
          <PageBreadcrumb crumbs={[{ label: 'Płatności' }]} />
          <p {...cms.text('payment.eyebrow')} style={{ margin: '34px 0 14px', color: '#e05590', fontSize: 11, fontFamily: 'DM Sans, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            {cms.value('payment.eyebrow')}
          </p>
          <h1 {...cms.text('payment.heading')} style={{ maxWidth: 700, margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif', fontWeight: 500, fontSize: 'clamp(42px, 6vw, 72px)', lineHeight: 1 }}>
            {cms.value('payment.heading')}
          </h1>
          <p {...cms.text('payment.intro')} style={{ maxWidth: 650, margin: '24px 0 0', fontFamily: 'DM Sans, sans-serif', fontSize: 17, lineHeight: 1.75, color: 'rgba(42,31,20,0.72)' }}>
            {cms.value('payment.intro')}
          </p>
        </div>
      </section>

      <section className="page-container" style={{ paddingTop: 'clamp(40px, 6vw, 72px)', paddingBottom: 'clamp(40px, 6vw, 72px)' }}>
        <h2 {...cms.text('payment.methods')} style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif', fontWeight: 500, fontSize: 'clamp(30px, 4vw, 45px)' }}>
          {cms.value('payment.methods')}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18, marginTop: 28 }}>
          {PAYMENT_METHODS.map(({ icon: Icon, title, id }) => (
            <article key={title} style={{ padding: 26, background: '#fff', border: '1px solid rgba(210,60,120,0.16)', borderRadius: 4 }}>
              <Icon size={25} color="#e05590" aria-hidden />
              <h3 {...cms.text(`payment.${id}.title`)} style={{ margin: '18px 0 9px', fontFamily: 'Cormorant Garamond, Georgia, serif', fontWeight: 600, fontSize: 25 }}>{cms.value(`payment.${id}.title`)}</h3>
              <p {...cms.text(`payment.${id}.description`)} style={{ margin: 0, fontFamily: 'DM Sans, sans-serif', fontSize: 14, lineHeight: 1.7, color: 'rgba(42,31,20,0.7)' }}>{cms.value(`payment.${id}.description`)}</p>
            </article>
          ))}
        </div>
        <aside style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginTop: 30, padding: '20px 22px', background: '#fff', borderLeft: '3px solid #e05590' }}>
          <ShieldCheck size={24} color="#e05590" aria-hidden style={{ flexShrink: 0 }} />
          <div>
            <h2 {...cms.text('payment.safety.title')} style={{ margin: 0, fontFamily: 'DM Sans, sans-serif', fontSize: 15, fontWeight: 700 }}>{cms.value('payment.safety.title')}</h2>
            <p {...cms.text('payment.safety.description')} style={{ margin: '7px 0 0', fontFamily: 'DM Sans, sans-serif', fontSize: 14, lineHeight: 1.7, color: 'rgba(42,31,20,0.7)' }}>
              {cms.value('payment.safety.description')}
            </p>
          </div>
        </aside>
      </section>
      <Footer />
    </main>
  );
}
